import _ from "lodash";

const PLATFORM_LEVEL_KEYS = [
  "campaign",
  "adsGroups",
  "ads",
  "assetsGroups",
  "keywords",
  "webpages",
  "adTexts",
  "assets",
];

/**
 * levels[level].maximum[platform] -> { campaign: metrics } | { [entityId]: metrics }
 */
const buildLegacyPlatformSnapshot = (unifiedCampaign, platform) => {
  const snapshot = {};

  for (const levelKey of PLATFORM_LEVEL_KEYS) {
    const platformData =
      unifiedCampaign?.levels?.[levelKey]?.maximum?.[platform];

    if (!platformData || _.isEmpty(platformData)) {
      continue;
    }

    if (levelKey === "campaign") {
      snapshot.campaign = platformData.campaign || {};
      continue;
    }

    snapshot[levelKey] = { ...platformData };
  }

  return snapshot;
};

/**
 * Maps unified response to legacy getStatisticsAdsCampaigns shape:
 * { [campaignID]: { [platform]: snapshot } }
 */
export const mapUnifiedToLegacyCampaignsStatsMap = (unifiedData) => {
  const campaigns = unifiedData?.campaigns || {};
  const result = {};

  for (const campaignID of Object.keys(campaigns)) {
    const unifiedCampaign = campaigns[campaignID];
    result[campaignID] = {};

    const platforms = unifiedCampaign?.platforms || [];
    for (const platform of platforms) {
      const snapshot = buildLegacyPlatformSnapshot(unifiedCampaign, platform);
      if (!_.isEmpty(snapshot)) {
        result[campaignID][platform] = snapshot;
      }
    }
  }

  return result;
};

/**
 * Maps unified response to legacy getStatisticsAdsCampaign shape:
 * { historical: {}, total: { [platform]: snapshot } }
 */
export const mapUnifiedToLegacySingleCampaignStats = (
  unifiedData,
  campaignID
) => {
  const unifiedCampaign = unifiedData?.campaigns?.[campaignID];
  const total = {};

  if (!unifiedCampaign) {
    return { historical: {}, total: {} };
  }

  for (const platform of unifiedCampaign.platforms || []) {
    const snapshot = buildLegacyPlatformSnapshot(unifiedCampaign, platform);
    if (!_.isEmpty(snapshot)) {
      total[platform] = snapshot;
    }
  }

  return {
    historical: {},
    total,
  };
};

/**
 * Maps unified daily campaign-level stats to legacy timeSeries shape.
 * levels.campaign.daily[platform].campaign -> array of buckets
 */
export const mapUnifiedToLegacyTimeSeries = (unifiedData, campaignIDs = []) => {
  const byCampaign = {};
  let buckets = [];

  for (const campaignID of campaignIDs) {
    const unifiedCampaign = unifiedData?.campaigns?.[campaignID];
    if (!unifiedCampaign) {
      continue;
    }

    byCampaign[campaignID] = {};

    for (const platform of unifiedCampaign.platforms || []) {
      const campaignBuckets =
        unifiedCampaign?.levels?.campaign?.daily?.[platform]?.campaign || [];

      if (!buckets.length && campaignBuckets.length) {
        buckets = campaignBuckets.map((bucket) => ({
          startDateTime: bucket.startDateTime,
          endDateTime: bucket.endDateTime,
        }));
      }

      byCampaign[campaignID][platform] = campaignBuckets.map((bucket) => ({
        campaign: bucket.value || {},
      }));
    }
  }

  if (!buckets.length) {
    return null;
  }

  return { buckets, byCampaign };
};

/**
 * Request presets matching legacy endpoint behavior.
 */
export const LEGACY_CAMPAIGNS_STATS_LEVELS = {
  campaign: true,
  adsGroups: false,
  ads: false,
  assetsGroups: false,
  keywords: false,
  webpages: false,
  adTexts: false,
  assets: false,
};

export const LEGACY_CAMPAIGNS_STATS_BREAKDOWNS = {
  campaign: ["maximum"],
  adsGroups: [],
  ads: [],
  assetsGroups: [],
  keywords: [],
  webpages: [],
  adTexts: [],
  assets: [],
};

export const LEGACY_SINGLE_CAMPAIGN_STATS_LEVELS = {
  campaign: true,
  adsGroups: true,
  ads: true,
  assetsGroups: true,
  keywords: false,
  webpages: false,
  adTexts: false,
  assets: false,
};

export const LEGACY_SINGLE_CAMPAIGN_STATS_BREAKDOWNS = {
  campaign: ["maximum"],
  adsGroups: ["maximum"],
  ads: ["maximum"],
  assetsGroups: ["maximum"],
  keywords: ["maximum"],
  webpages: ["maximum"],
  adTexts: ["maximum"],
  assets: ["maximum"],
};

export const LEGACY_CAMPAIGNS_TIME_SERIES_BREAKDOWNS = {
  ...LEGACY_CAMPAIGNS_STATS_BREAKDOWNS,
  campaign: ["maximum", "daily"],
};

/** Global Report core: campaign maximum + daily (KPIs, platforms, time series, goals) */
export const GLOBAL_REPORT_STATS_LEVELS = {
  campaign: true,
  adsGroups: false,
  ads: false,
  assetsGroups: false,
  keywords: false,
  webpages: false,
  adTexts: false,
  assets: false,
};

export const GLOBAL_REPORT_STATS_BREAKDOWNS = {
  campaign: ["maximum", "daily"],
  adsGroups: [],
  ads: [],
  assetsGroups: [],
  keywords: [],
  webpages: [],
  adTexts: [],
  assets: [],
};

/** Global Report creatives: ads maximum only (lazy, post activity filter) */
export const GLOBAL_REPORT_ADS_STATS_LEVELS = {
  campaign: false,
  adsGroups: false,
  ads: true,
  assetsGroups: false,
  keywords: false,
  webpages: false,
  adTexts: false,
  assets: false,
};

export const GLOBAL_REPORT_ADS_STATS_BREAKDOWNS = {
  campaign: [],
  adsGroups: [],
  ads: ["maximum"],
  assetsGroups: [],
  keywords: [],
  webpages: [],
  adTexts: [],
  assets: [],
};

/** Combined campaign + ads (legacy helpers that still request both together) */
export const GLOBAL_REPORT_STATS_WITH_ADS_LEVELS = {
  ...GLOBAL_REPORT_STATS_LEVELS,
  ads: true,
};

export const GLOBAL_REPORT_STATS_WITH_ADS_BREAKDOWNS = {
  ...GLOBAL_REPORT_STATS_BREAKDOWNS,
  ads: ["maximum"],
};
